/* Normalizes the new high-end syllabus response into coherent course/sessions/assignments */

const DAY_MAP = {
  sunday: 'SU', monday: 'MO', tuesday: 'TU', wednesday: 'WE',
  thursday: 'TH', friday: 'FR', saturday: 'SA'
};

function parseMeetingTimeLabel(label = '') {
  // Example: "Wednesdays 19:00–21:30"
  const lower = label.toLowerCase();
  const dayKey = Object.keys(DAY_MAP).find(d => lower.includes(d));
  const byday = dayKey ? [DAY_MAP[dayKey]] : [];
  const timeMatch = label.match(/(\d{1,2}:\d{2})\s*[–-]\s*(\d{1,2}:\d{2})/);
  const meetingStartTime = timeMatch ? timeMatch[1] : '';
  const meetingEndTime = timeMatch ? timeMatch[2] : '';
  return { byday, meetingStartTime, meetingEndTime, meetingTimeLabel: label };
}

function buildWeeklyRRule(byday = [], count) {
  const parts = ['FREQ=WEEKLY'];
  if (byday.length) parts.push(`BYDAY=${byday.join(',')}`);
  if (count && Number(count) > 0) parts.push(`COUNT=${count}`);
  return parts.join(';');
}

function toISO(date, time = '00:00') {
  if (!date) return '';
  const t = time.length === 5 ? `${time}:00` : time;
  return `${date}T${t}`;
}

function parseWeightPercent(weight) {
  if (!weight) return null;
  const m = String(weight).match(/(\d+(\.\d+)?)\s*%/);
  return m ? Number(m[1]) : null;
}

export function normalizeSyllabusResponse(response) {
  const r = response?.syllabus_data || response || {};
  const ci = r.course_information || {};

  // Meeting time: Use structured fields first, then fall back to parsing text field
  let meetingStartTime = ci.meeting_start_time || '';
  let meetingEndTime = ci.meeting_end_time || '';
  let byday = [];
  let meetingTimeLabel = '';
  
  if (meetingStartTime && meetingEndTime) {
    // Have structured time fields - still need to parse days from text if present
    const textField = ci.meeting_time || ci.meeting_times || '';
    if (textField) {
      const parsed = parseMeetingTimeLabel(textField);
      byday = parsed.byday;
      meetingTimeLabel = textField;
    }
  } else {
    // Fall back to parsing text field for both times and days
    const textField = ci.meeting_time || ci.meeting_times || '';
    const parsed = parseMeetingTimeLabel(textField);
    byday = parsed.byday;
    meetingStartTime = parsed.meetingStartTime;
    meetingEndTime = parsed.meetingEndTime;
    meetingTimeLabel = parsed.meetingTimeLabel;
  }

  // Sessions
  const sessions = Array.isArray(r.class_sessions) ? r.class_sessions : [];
  const dates = sessions.map(s => s.startDate).filter(Boolean).sort();
  const startDate = dates[0] || '';
  const endDate = dates[dates.length - 1] || '';

  // Treat explicit “No Class” / holiday titles as canceled
  const normalizedSessions = sessions.map((s, idx) => {
    const title = s.title || `Session ${idx + 1}`;
    const isHoliday = /no class|holiday/i.test(title);
    return {
      sequence: idx + 1,
      date: s.startDate || '',
      startTime: meetingStartTime || '',
      endTime: meetingEndTime || '',
      title,
      description: s.description || '',
      topics: Array.isArray(s.topics) ? s.topics : [],
      readings: Array.isArray(s.readings) ? s.readings : [],
      canceled: Boolean(isHoliday),
    };
  });

  // Course
  // Extract course code from AI response or parse from course name
  let courseCode = ci.course_code || '';
  if (!courseCode && ci.course_name) {
    const m = ci.course_name.match(/^\s*([A-Za-z]{1,6}\s?-?\d{2,6}(?:\.\d+)?)/);  
    if (m && m[1]) {
      courseCode = m[1].trim();
    }
  }
  
  const course = {
    courseName: ci.course_name || '',
    courseCode: courseCode,
    section: '',
    semester: (ci.course_name || '').match(/fall\s*20\d{2}/i)?.[0] || '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    location: ci.location || '',
    instructorPrimary: ci.instructor || '',
    instructors: ci.instructor ? [{ name: ci.instructor, role: 'instructor' }] : [],
    emails: Array.isArray(ci.emails) ? ci.emails : [],
    phone: ci.phone || '',
    courseWebsite: ci.course_website || '',
    officeHours: ci.office_hours || '',
    meetingDays: byday,
    meetingStartTime,
    meetingEndTime,
    meetingTimeLabel,
    startDate,
    endDate,
    rrule: buildWeeklyRRule(byday, normalizedSessions.length || undefined),
    holidays: normalizedSessions.filter(s => s.canceled).map(s => s.date),
    cancellations: [],
    textbooks: [],
    confidence: null,
  };

  // Assignments from sessions (mix of strings and objects)
  const sessionAssignments = normalizedSessions.flatMap(s => {
    const items = Array.isArray(sessions[s.sequence - 1]?.assignments)
      ? sessions[s.sequence - 1].assignments
      : [];
    return items.map((item) => {
      let title = '';
      let description = '';
      let assignmentType = 'assignment';
      let points = null;
      if (typeof item === 'string') {
        title = item;
        description = '';
      } else if (item && typeof item === 'object') {
        title = item.title || 'Untitled';
        description = item.description || '';
        assignmentType = item.assignment_type || item.type || 'assignment';
        points = item.points ?? null;
      }
      // “due before class” heuristic: due at session start time
      const dueDatetime = s.date ? toISO(s.date, s.startTime || meetingStartTime || '00:00') : '';
      return {
        title,
        description,
        assignmentType,
        dueDatetime,
        points,
        weightPercent: null,
        groupWork: /team|group/i.test(title + ' ' + description),
        sessionSequence: s.sequence,
        source: 'SYLLABUS_SESSION',
      };
    });
  });

  // Get last class session date for standalone assignments with null due dates
  const lastSessionDate = sessions.length > 0 
    ? sessions[sessions.length - 1].startDate 
    : '';
  const lastSessionEndOfDay = lastSessionDate 
    ? toISO(lastSessionDate.split('T')[0], '23:59') 
    : '';

  // Standalone assignments with weights/checkpoints
  const standalone = Array.isArray(r.standalone_assignments) ? r.standalone_assignments : [];
  const standaloneAssignments = standalone.flatMap(sa => {
    const base = {
      title: sa.title || 'Untitled',
      description: sa.description || '',
      weightPercent: parseWeightPercent(sa.weight),
      dueInstructions: sa.due_instructions || '',
      assignmentType: /presentation/i.test(sa.title || '') ? 'presentation'
        : /participation/i.test(sa.title || '') ? 'assignment'
        : /project/i.test(sa.title || '') ? 'assignment'
        : 'assignment',
      groupWork: /group/i.test(sa.title || '') || /team/i.test(sa.description || ''),
      source: 'SYLLABUS_STANDALONE',
    };

    // Deliverables array (e.g., checkpoints)
    const deliverables = Array.isArray(sa.deliverables) ? sa.deliverables : [];
    if (!deliverables.length) {
      // Use due_at if present, otherwise use last session date
      const dueDateTime = sa.due_at || lastSessionEndOfDay;
      return [{
        ...base,
        dueDatetime: dueDateTime,
        sessionSequence: null,
      }];
    }
    return deliverables.map(d => {
      const approx = d.approx_due || d.approx_date || '';
      const dateMatch = String(approx).match(/20\d{2}-\d{2}-\d{2}/) ||
                        String(approx).match(/(20\d{2}-\d{2}-\d{2})/);
      const date = dateMatch ? dateMatch[0] : '';
      return {
        ...base,
        title: d.name || base.title,
        weightPercent: parseWeightPercent(d.weight) ?? base.weightPercent,
        dueDatetime: date ? toISO(date, '23:59') : '',
        sessionSequence: null,
      };
    });
  });

  // Exams/finals may imply assignments with due on specified dates
  const exams = Array.isArray(r.exams) ? r.exams : [];
  const examAssignments = exams.flatMap(ex => {
    const weightPercent = parseWeightPercent(ex.weight);
    const title = ex.title || 'Exam';
    const description = ex.description || '';
    const dates = Array.isArray(ex.dates) ? ex.dates : [];
    if (dates.length) {
      return dates.map(d => ({
        title,
        description,
        assignmentType: /presentation/i.test(title) ? 'presentation' : 'exam',
        dueDatetime: toISO(d, '23:59'),
        points: null,
        weightPercent,
        groupWork: /group/i.test(title + ' ' + description),
        sessionSequence: null,
        source: 'SYLLABUS_STANDALONE',
      }));
    }
    // components (checkpoints) — make assignment entries as well
    const comps = Array.isArray(ex.components) ? ex.components : [];
    return comps.map(c => ({
      title: c.name || title,
      description,
      assignmentType: /checkpoint|check-in|check in/i.test(c.name || '') ? 'checkpoint' : 'assignment',
      dueDatetime: c.approx_date ? toISO(c.approx_date, '23:59') : '',
      points: null,
      weightPercent: parseWeightPercent(c.weight),
      groupWork: /group|team/i.test(title + ' ' + description + ' ' + c.name),
      sessionSequence: null,
      source: 'SYLLABUS_STANDALONE',
    }));
  });

  const assignments = [...sessionAssignments, ...standaloneAssignments, ...examAssignments];

  return {
    course,
    sessions: normalizedSessions,
    assignments,
    raw: response,
    meta: {
      sessionCount: normalizedSessions.length,
      assignmentCount: assignments.length,
    },
  };
}